import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CheckCircle, XCircle, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

const ROLE_DEST = {
  admin: "/admin",
  designer: "/designer",
  vendor: "/vendor",
  client: "/dashboard",
};

export default function InviteAccept() {
  const { token } = useParams();
  const nav = useNavigate();
  const { login } = useAuth();
  const [invite, setInvite] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get(`/invites/${token}`)
      .then((r) => { if (!cancelled) setInvite(r.data); })
      .catch((e) => { if (!cancelled) setLoadError(e.response?.data?.detail || "Invitation could not be loaded"); });
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/invites/${token}/accept`, { password });
      login(data.token, data.user);
      toast.success("Account activated!");
      nav(ROLE_DEST[data.user.role] || "/dashboard", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Activation failed");
    } finally { setBusy(false); }
  };

  if (loadError) {
    return (
      <div className="min-h-screen bg-[var(--pf-bg)] flex items-center justify-center p-6">
        <div className="max-w-md w-full border border-[var(--pf-border)] bg-white rounded-2xl p-8 text-center" data-testid="invite-error">
          <XCircle size={48} weight="duotone" className="mx-auto text-red-500 mb-4"/>
          <h1 className="text-2xl font-black tracking-tight">Invitation unavailable</h1>
          <p className="text-[var(--pf-text-2)] mt-2 text-sm">{loadError}</p>
          <button onClick={() => nav("/")} className="pf-btn-secondary mt-6" data-testid="invite-back-home">
            Back to home
          </button>
        </div>
      </div>
    );
  }

  if (!invite) {
    return (
      <div className="min-h-screen bg-[var(--pf-bg)] flex items-center justify-center">
        <div className="pf-label" data-testid="invite-loading">Loading invitation…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--pf-bg)] flex items-center justify-center p-4 sm:p-6">
      <div className="max-w-md w-full">
        <div className="text-center mb-6">
          <div className="inline-block font-black text-3xl tracking-tighter">
            TALAIO<span className="text-[var(--pf-brand)]">/AI</span>
          </div>
        </div>
        <div className="border border-[var(--pf-border)] bg-white rounded-2xl p-6 sm:p-8" data-testid="invite-form">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-lg bg-[var(--pf-brand)]/10 text-[var(--pf-brand)] flex items-center justify-center">
              <Sparkle size={20} weight="fill"/>
            </div>
            <div>
              <div className="pf-label">You're invited</div>
              <div className="text-xs text-[var(--pf-text-3)]">as a {invite.role_label}</div>
            </div>
          </div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight break-words">Welcome, {invite.name}</h1>
          <p className="text-sm text-[var(--pf-text-2)] mt-2">
            Set a password to activate your <strong>{invite.role_label}</strong> account.
          </p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <label className="pf-label block mb-1">Email</label>
              <input value={invite.email} disabled className="w-full border border-[var(--pf-border)] rounded-lg p-3 bg-[var(--pf-surface-2)] text-[var(--pf-text-2)] text-sm" data-testid="invite-email"/>
            </div>
            <div>
              <label className="pf-label block mb-1">Choose a password</label>
              <input
                type="password"
                value={password}
                onChange={(e)=>setPassword(e.target.value)}
                minLength={6}
                required
                autoFocus
                className="w-full border border-[var(--pf-border)] rounded-lg p-3 text-sm focus:outline-none focus:border-[var(--pf-text)]"
                data-testid="invite-password"
              />
            </div>
            <div>
              <label className="pf-label block mb-1">Confirm password</label>
              <input
                type="password"
                value={confirm}
                onChange={(e)=>setConfirm(e.target.value)}
                minLength={6}
                required
                className="w-full border border-[var(--pf-border)] rounded-lg p-3 text-sm focus:outline-none focus:border-[var(--pf-text)]"
                data-testid="invite-confirm"
              />
            </div>
            <button type="submit" disabled={busy} className="pf-btn-primary w-full" data-testid="invite-submit">
              {busy ? "Activating…" : (<><CheckCircle size={16} weight="fill"/> Activate account</>)}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
